/**
 * API Error Prototype abstracting API returned response error
 *
 * ==============================================
 *
 * (!) Due to few JSDoc/TS limitations, use `~/utils/infra/common/helpers`
 *     optics for <APIError.cause> and <APIError.message>
 *
 * ==============================================
 *
 * (?) Business pattern matching with Request APIError
 *
 *    ```
 *      const isTargetedError
 *        =  error instanceof APIError
 *        && error.cause === `my targeted error cause`
 *    ```
 *
 * ==============================================
 *
 * (?) Business tapping log
 *
 *    ```
 *      console.log(error)          // print out as a common <Error>
 *      console.log(error.message)  // print out common <Error.message>
 *      console.log(error.cause)    // print out native extra <Error.cause>
 *    ```
 */
import { clone, compose, isEmpty, not } from '~/utils/lib/core/prelude'

/**
 * API Error Constructor
 * @constructor
 * @param {string} cause eg. "ResponseError"
 * @param {string} message eg. "Requête HTTP en erreur"
 * @param {any} [payload] ie. <RequestResponse.data>
 * @extends {Error}
 * @link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error#ES5_Custom_Error_Object
 */
// @ts-ignore: JSDoc ES5 Prototype Constructor handling
// @see https://github.com/microsoft/TypeScript/issues/36369
// @see https://github.com/microsoft/TypeScript/issues/13206
function APIError(cause, message, payload = undefined) {
  // @ts-ignore: typing false negative, as <Error> can bear optional second
  //             argument
  const instance = new Error(message, { cause })

  if (compose(not, isEmpty)(payload))
    // @ts-ignore: quick extension of <Error> that obviously not handled by
    //             typing definition
    instance.payload = clone(payload)

  Object.setPrototypeOf(instance, Object.getPrototypeOf(this))

  return instance
}

APIError.prototype = Object.create(Error.prototype, {
  constructor: {
    value: Error,
    enumerable: false,
    writable: true,
    configurable: true
  }
})

export default APIError
